/**
 * @file PickItemRow.tsx
 * @description Строка позиции в задании на сборку (phase19.14, экран B шага 0).
 *
 * Собранная строка сворачивается до одной строки текста — кладовщику важно
 * видеть, что осталось, а не что уже сделано. Кнопки ± по 56px: на складе
 * работают в перчатках.
 *
 * Порядок строк задаёт сервер (маршрут обхода по `binSortKey`) — компонент
 * ничего не сортирует.
 */

import { AlertTriangle, Check, Minus, Package, Plus, RotateCcw } from "lucide-react";

import { StockHintLine } from "./StockHintLine";
import { cn } from "@/lib/utils";
import { Button } from "../../ui/button";
import { LIST_SURFACE_CLASS, ROW_SURFACE_TINT } from "../../ui/ListStates";
import {
  PICKING_SHORTAGE_REASON_LABEL,
  formatPickItemName,
  isItemPicked,
  type PickingTaskItemDto,
} from "../../../lib/api-picking";
import { formatItemQty, formatQtyValue } from "../../../lib/picking-format";

interface Props {
  item: PickingTaskItemDto;
  /** id узла — автоскролл к строке после скана. */
  id: string;
  /** false — задание закрыто: показываем факт, кнопки прячем. */
  editable: boolean;
  busy: boolean;
  /** Подсветка после скана — строка, которую только что отметили. */
  highlighted: boolean;
  onChangeQty: (qty: number) => void;
  onOpenManualQty: () => void;
  onOpenShortage: () => void;
  /** Снять отметку недостачи: товар нашёлся, строку возвращаем в работу. */
  onClearShortage: () => void;
}

export function PickItemRow({
  item,
  id,
  editable,
  busy,
  highlighted,
  onChangeQty,
  onOpenManualQty,
  onOpenShortage,
  onClearShortage,
}: Props) {
  const planned = Number(item.qtyPlanned);
  const picked = Number(item.qtyPicked);
  const shortage = Number(item.shortageQty);
  const done = isItemPicked(item);
  // Строка закрыта недостачей: вместо степпера показываем один выход —
  // «Снять отметку» (`shortageQty: 0` на сервере, phase19.24 довесок шага 4).
  // У задания в статусе SHORTAGE `editable` уже false: там строки правятся
  // только после возврата задания в очередь.
  const locked = shortage > 0;

  return (
    <li
      id={id}
      className={cn(
        "rounded-xl border p-3 transition-colors",
        LIST_SURFACE_CLASS,
        // SC69: подложки непрозрачные, а собранная строка приглушает только
        // содержимое (`opacity` на всей карточке пропускал фон кабинета).
        highlighted
          ? cn("border-primary", ROW_SURFACE_TINT.selected)
          : done
            ? "border-on-surface/8 [&>*]:opacity-70"
            : "border-on-surface/8",
        // Строка закрыта недостачей — состояние фоном и ring, а не бордером
        // (phase19.24 §3.3): собирать по ней нечего, пока отметку не снимут.
        shortage > 0 && cn("border-transparent ring-1 ring-destructive/40", ROW_SURFACE_TINT.danger),
      )}
    >
      <div className="flex gap-3">
        {item.sku.imageUrl ? (
          <img
            src={item.sku.imageUrl}
            alt=""
            className="size-16 shrink-0 rounded-lg object-cover"
          />
        ) : (
          <div className="flex size-16 shrink-0 items-center justify-center rounded-lg bg-surface-low">
            <Package className="size-6 text-on-surface-variant" aria-hidden />
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-start justify-between gap-2">
            <span className="min-w-0 text-base font-medium">
              {formatPickItemName(item)}
            </span>
            <span className="shrink-0 text-sm tabular-nums">
              {done ? <Check className="inline size-4 text-primary" /> : null}{" "}
              {formatItemQty(item.qtyPicked, item.qtyPlanned, item.unit)}
            </span>
          </div>
          <span className="font-mono text-sm text-on-surface-variant">
            Арт. {item.skuCode}
          </span>

          {/* «Откуда брать» — один реестр формулировок с таблицей на lg
              (phase19.24 §3.1): ячейка, партия, остаток или «Остатка нет». */}
          <StockHintLine item={item} />

          {shortage > 0 ? (
            <span className="flex items-start gap-1.5 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <span>
                Недостача {formatItemQty(item.shortageQty, item.qtyPlanned, item.unit)}
                {item.shortageReason
                  ? ` · ${PICKING_SHORTAGE_REASON_LABEL[item.shortageReason]}`
                  : ""}
                {item.note ? ` · ${item.note}` : ""}
              </span>
            </span>
          ) : null}
        </div>
      </div>

      {editable && locked ? (
        <div className="mt-3">
          <Button
            variant="outline"
            className="min-h-14 w-full sm:w-auto"
            onClick={onClearShortage}
            disabled={busy}
          >
            <RotateCcw className="size-4" />
            <span className="ml-1.5">Снять отметку</span>
          </Button>
        </div>
      ) : null}

      {editable && !done && !locked ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center overflow-hidden rounded-xl border border-on-surface/15">
            <button
              type="button"
              onClick={() => onChangeQty(Math.max(0, picked - 1))}
              disabled={busy || picked <= 0}
              aria-label="Убрать одну единицу"
              className="inline-flex size-14 items-center justify-center disabled:opacity-40"
            >
              <Minus className="size-5" />
            </button>
            <button
              type="button"
              onClick={onOpenManualQty}
              disabled={busy}
              aria-label="Ввести количество вручную"
              className="inline-flex h-14 min-w-[64px] items-center justify-center border-x border-on-surface/15 px-2 text-base tabular-nums"
            >
              {formatQtyValue(item.qtyPicked)}
            </button>
            <button
              type="button"
              onClick={() => onChangeQty(Math.min(planned, picked + 1))}
              disabled={busy || picked >= planned}
              aria-label="Добавить одну единицу"
              className="inline-flex size-14 items-center justify-center disabled:opacity-40"
            >
              <Plus className="size-5" />
            </button>
          </div>
          <Button
            variant="outline"
            className="min-h-14 text-destructive"
            onClick={onOpenShortage}
            disabled={busy}
          >
            <AlertTriangle className="size-4" />
            <span className="ml-1.5">Отметить недостачу</span>
          </Button>
        </div>
      ) : null}
    </li>
  );
}
